import { useCallback, useEffect, useRef, useState } from 'react';

interface UsePreviousSearchProps {
  qsgSearchTerm: string;
  qsgSearchPage: number;
  currentSectionId: string;
}

interface UsePreviousSearchReturn {
  cameFromSearch: boolean;
  markSearchResultClick: () => void;
  restoreSearch: () => { searchTerm: string; searchPage: number } | null;
}

/**
 * Custom hook to track if we navigated from search results to a section
 * and provide functionality to restore the search state
 */
export const usePreviousSearch = ({
  qsgSearchTerm,
  qsgSearchPage,
  currentSectionId,
}: UsePreviousSearchProps): UsePreviousSearchReturn => {
  const [cameFromSearch, setCameFromSearch] = useState(false);
  const cameFromSearchRef = useRef(false);
  const pendingSearchResultClickRef = useRef(false);
  const savedSearchTermRef = useRef<string>('');
  const savedSearchPageRef = useRef<number>(1);
  const previousSectionRef = useRef(currentSectionId);

  const setCameFromSearchValue = useCallback((value: boolean) => {
    cameFromSearchRef.current = value;
    setCameFromSearch(value);
  }, []);

  /**
   * Call this ONLY when a user clicks a search result item (not when they
   * navigate via the sidebar/navigation while search is open).
   */
  const markSearchResultClick = useCallback(() => {
    pendingSearchResultClickRef.current = true;
    savedSearchTermRef.current = qsgSearchTerm || '';
    savedSearchPageRef.current = qsgSearchPage;
  }, [qsgSearchPage, qsgSearchTerm]);

  // Detect when we navigate from search -> section after a search-result click
  useEffect(() => {
    const hasSearchTerm = Boolean(qsgSearchTerm);
    const hasSection = Boolean(currentSectionId);
    const hadSection = Boolean(previousSectionRef.current);
    const sectionChanged = currentSectionId !== previousSectionRef.current;

    // If we're in search, we haven't "come from" search results yet.
    if (hasSearchTerm) {
      pendingSearchResultClickRef.current = false;
      if (cameFromSearchRef.current) setCameFromSearchValue(false);
    } else if (pendingSearchResultClickRef.current && hasSection) {
      // Search term is now cleared, section is set, and we know it was from a result click.
      pendingSearchResultClickRef.current = false;
      setCameFromSearchValue(true);
    } else if (!hasSection && hadSection) {
      // If we navigated from section to navigation (no section), clear the flag
      // This happens when opening the sidebar/navigation
      if (cameFromSearchRef.current) setCameFromSearchValue(false);
      pendingSearchResultClickRef.current = false;
    } else if (sectionChanged && hadSection && hasSection) {
      // If we're navigating from one section to another (not from search),
      // reset the flag because we're no longer directly from search
      if (cameFromSearchRef.current) setCameFromSearchValue(false);
    } else if (sectionChanged && !hadSection && hasSection) {
      // If we navigated from navigation (no section) to a section,
      // clear the flag because we're navigating via navigation menu, not from search
      if (cameFromSearchRef.current) setCameFromSearchValue(false);
    }

    previousSectionRef.current = currentSectionId;
  }, [qsgSearchTerm, currentSectionId, qsgSearchPage, setCameFromSearchValue]);

  const restoreSearch = useCallback(() => {
    if (cameFromSearchRef.current && currentSectionId) {
      setCameFromSearchValue(false);

      // Return search params for navigation - caller should navigate to remove section from path
      // Don't dispatch here - let the URL update trigger the store sync via useQueryParamSync
      return {
        searchTerm: savedSearchTermRef.current,
        searchPage: savedSearchPageRef.current,
      };
    }
    return null;
  }, [currentSectionId, setCameFromSearchValue]);

  return {
    cameFromSearch,
    markSearchResultClick,
    restoreSearch,
  };
};
